import React, { Fragment } from 'react';

import { Word as WordType } from 'shared-types/lyrics';
import { colorUsage } from 'stylesheetB2c';

import {
  Cursor,
  CursorArea,
  SentenceContent,
  SentenceOutline,
  WORD_CLASS_NAME,
  WORD_OUTLINE_CLASS_NAME,
} from './Sentence.style';
import {
  getWordAnimationDelayInMs,
  getWordAnimationDurationInMs,
  getCursorAnimationDelayInMs,
} from './Sentence.utils';

interface Props {
  words: WordType[];
  isPlaying: boolean;
  swipeColor: string;
  hasCursor: boolean;
  wordOutlineFontSize: number;
  cursorAreaWidthInPx: number;
  cursorAreaRightSpacingInPx: number;
  blockStartOrLastManualTimeUpdate: number;
}

const Sentence: React.FC<Props> = ({
  words,
  isPlaying,
  swipeColor,
  hasCursor,
  wordOutlineFontSize,
  cursorAreaWidthInPx,
  cursorAreaRightSpacingInPx,
  blockStartOrLastManualTimeUpdate,
}) => {
  return (
    <SentenceContent>
      {words.map((word: WordType) => {
        const { wordStart, wordEnd, wordValue } = word;
        return (
          <Fragment key={`${wordStart}-${wordEnd}${wordValue}`}>
            <span
              className={WORD_CLASS_NAME}
              // We use inline style here for all dynamics css properties, to optimize player performances
              style={{
                animationDuration: `${getWordAnimationDurationInMs(wordStart, wordEnd)}ms`,
                animationDelay: `${getWordAnimationDelayInMs(
                  wordStart,
                  blockStartOrLastManualTimeUpdate,
                )}ms`,
                animationPlayState: isPlaying ? 'running' : 'paused',
                backgroundImage: `linear-gradient(
                  to right,
                  ${swipeColor},
                  50%,
                  ${colorUsage.mp3KbpPlayer.lyricsBackground} 50%
                )`,
              }}
            >
              {wordValue}
            </span>
            {/*
              Words from lyrics.json file can be split by syllable (ex: "c'é", "tait" for "c'était")
              When corresponding to a full word, the wordValue ends with a space character (ex: "c'était ")
              Hence, we check the last character to determine whether we should insert a space after the word or not
            */}
            {wordValue[wordValue.length - 1] === ' ' && <>&nbsp;</>}
          </Fragment>
        );
      })}
      <SentenceOutline wordOutlineFontSize={wordOutlineFontSize}>
        {words.map(({ wordStart, wordEnd, wordValue }) => (
          <span
            key={`${wordStart}-${wordEnd}-${wordValue.length}`}
            className={WORD_OUTLINE_CLASS_NAME}
          >
            {wordValue}
          </span>
        ))}
      </SentenceOutline>
      {hasCursor && (
        <CursorArea widthInPx={cursorAreaWidthInPx} rightSpacingInPx={cursorAreaRightSpacingInPx}>
          <Cursor
            swipeColor={swipeColor}
            delayInMs={getCursorAnimationDelayInMs(
              words[0].wordStart,
              blockStartOrLastManualTimeUpdate,
            )}
            isPlaying={isPlaying}
          />
        </CursorArea>
      )}
    </SentenceContent>
  );
};

export default Sentence;
